/**
 * Mock data for the Dealer Areas (territories) admin module.
 *
 * Each area is owned by exactly one branch (typically a sales/HQ branch).
 * Dealers are linked to areas via `mockDealers[*].dealerAreaId` (added on
 * `MockDealer` in `salesMock.ts`).
 *
 * 8 areas covering Bangladesh's 8 traditional divisions, distributed across
 * the 5 mock branches in `inventoryMock.ts` so the manage-areas page shows
 * realistic spread.
 */

import { emptyIfInstant } from "./mockMode";
import { mockDealers } from "./salesMock";

const NOW = new Date("2026-05-09T08:00:00Z").getTime();
const DAY = 24 * 60 * 60 * 1000;

export type MockDealerArea = {
  _id: string;
  code: string;
  displayName: string;
  branchId: string;
  district: string;
  description: string;
  isActive: boolean;
  createdAt: number;
  updatedAt: number;
  deletedAt: number | null;
};

export const mockDealerAreas: MockDealerArea[] = emptyIfInstant<MockDealerArea>([]);

/** Helper: area lookup by id. */
export function findArea(id: string): MockDealerArea | null {
  return mockDealerAreas.find((a) => a._id === id) ?? null;
}

/** Helper: areas managed by a given branch. */
export function areasForBranch(branchId: string): MockDealerArea[] {
  return mockDealerAreas.filter(
    (a) => a.branchId === branchId && a.deletedAt === null,
  );
}

/** Helper: dealers currently in an area. */
export function dealersForArea(areaId: string): typeof mockDealers {
  return mockDealers.filter((d) => (d.dealerAreaId ?? null) === areaId);
}

/** Helper: count of dealers per area, keyed by area id. */
export function dealerCountByArea(): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const d of mockDealers) {
    const aid = d.dealerAreaId ?? null;
    if (aid) {
      counts[aid] = (counts[aid] ?? 0) + 1;
    }
  }
  return counts;
}
